import React, { useContext, useEffect } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { LocationDetailPlanOutDTO, OrderDetailPlanOutDTO } from '@api/ringil-api.v2';
import { formatVolume, formatWeight, isNotBlank, weightAndDimensions } from '@utils/utils';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { useTranslation } from 'react-i18next';
import Label from '@components/obsolete/Form/Label/Label.tsx';
import LoadInfoItem from '@components/obsolete/LoadDetails/LoadInfo/LoadInfoItem.tsx';
import LoadInfoList from '@components/obsolete/LoadDetails/LoadInfoList/LoadInfoList.tsx';
import Address from '@components/obsolete/Address/Address.tsx';
import { ExpeditionDetailHandlerContext } from '@app/pages/carrier/expedition-detail/ExpeditionDetailHandler.tsx';
import NavTitle from '@components/obsolete/NavTitle/NavTitle.tsx';
import CardView from '@components/obsolete/CardView/CardView.tsx';
import SelectedTruckType from '@components/SelectedTruckType.tsx';
import ButtonLayout from '@components/obsolete/Form/ButtonLayout/ButtonLayout.tsx';
import CheckboxItem from '@components/obsolete/CheckboxWrapper/CheckboxItem.tsx';
import InfoProvided from '@components/obsolete/InfoProvided/InfoProvided.tsx';
import InfoProvidedItem from '@components/obsolete/InfoProvided/InfoProvidedItem.tsx';
import AccordionTitle from '@components/obsolete/AccordionTitle/AccordionTitle.tsx';
import { ExpeditionContactInfo } from '@components/obsolete/common/ExpeditionContactInfo.tsx';
import MultipleValues from '@components/ringil3/HelperComponents/MultipleValues.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import CollapsibleNote from '@components/RingilComponents/RichTextEditor/CollapsibleNote.tsx';

interface LoadingUloadingDetailProps {
  loading: boolean;
  order: OrderDetailPlanOutDTO;
  carrierCode?: string;
  isFirstElement?: boolean;
  showVolume?: boolean;
}

const LoadingUloadingDetail: React.FC<LoadingUloadingDetailProps> = ({ loading, order, carrierCode = '-', isFirstElement = true, showVolume = false }) => {
  const { t }: { t: any } = useTranslation('common');
  const { tr } = useTranslationLgs();
  return (
    <div className="p-grid row-accordion-info">
      <div className="p-col-12 p-md-5 p-lg-4">
        <div className="p-d-flex">
          <div className="icon-accordion">{isFirstElement && <img src={loading ? '/images/icon_pickup.png' : '/images/icon_dropoff.png'} alt="" />}</div>
          <div>
            {isFirstElement && <Label title={loading ? t('TabExpeditionRoute.pickup', 'Pickup') : t('TabExpeditionRoute.dropoff', 'Dropoff')} />}
            <LoadInfoItem
              bookingRef={order.orderName}
              className="p-mb-3"
              totalWeight={formatWeight(order.totalWeight)}
              totalVolume={showVolume ? formatVolume(order.totalVolume) : undefined}
            />
          </div>
        </div>
      </div>
      <div className="p-col-12 p-md-5 p-lg-4">
        {order?.items?.map((orderItem, orderItemIndex) => {
          return (
            <React.Fragment key={orderItemIndex}>
              <LoadInfoList
                title={tr(`DemandShipmentDetail.cargoTemplateName`, `{{unit}} ({{count}} x)`, {
                  unit: orderItem.name,
                  count: orderItem.amount,
                })}
                description={weightAndDimensions(orderItem.height, orderItem.length, orderItem.width, orderItem.weight, t)}
                stackable={orderItem.stackable}
                itemNote={orderItem.itemNote}
              />
              {orderItem.manipulationUnitInfo && orderItem.manipulationUnitInfo.length > 0 && (
                <div>
                  <span>{tr(`RingilOrder.manipulationUnitInfoLabel`, `Manipulation units:`)}</span>
                  <div className="flex flex-col">
                    {orderItem.manipulationUnitInfo
                      .map(mu =>
                        [
                          isNotBlank(mu.externalId) ? mu.externalId : null,
                          isNotBlank(mu.name) ? mu.name : null,
                          mu.count != null ? `${mu.count}` : null,
                          mu.weight != null && mu.weight > 0 ? formatWeight(mu.weight) : null,
                          isNotBlank(mu.size) ? mu.size : null,
                          isNotBlank(mu.wheelbase) ? mu.wheelbase : null,
                        ]
                          .filter(Boolean)
                          .join(', ')
                      )
                      .map((unit, unitIndex) => <span key={unitIndex}>{unit}</span>)}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

interface ContactInfoProps {
  location: LocationDetailPlanOutDTO;
}

const ContactInfo: React.FC<ContactInfoProps> = ({ location }) => {
  const { t }: { t: any } = useTranslation('common');
  return (
    <div className="p-grid row-accordion-info">
      <div className="p-col-12 p-md-5 p-lg-4">
        <div className="p-d-flex">
          <div className="icon-accordion">
            <img src="/images/contacts.svg" alt="" />
          </div>
          <div>
            <Label title={t('expeditionOverviewPage.labelContacts')} />
            <LoadInfoItem name={location.contactName} phone={location.contactPhone} email={location.contactEmail} className="p-mb-3"></LoadInfoItem>
          </div>
        </div>
      </div>
      <div className="p-col-12 p-md-5 p-lg-4">
        <Address company={`${location.name}, ${location.address.city}`} address={`${location.address.streetNr}, ${location.address.zipCode}`} />
        {/*<Address company="Firma XYZ, České Budějovice" address="Sokolovská 8, 180 00" />*/}
        <COTimezone timezoneId={location.timezoneId} />
      </div>
    </div>
  );
};

const TabExpeditionRoute = () => {
  const { t }: { t: any } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const { state, loadShipmentPlan } = useContext(ExpeditionDetailHandlerContext);

  useEffect(() => {
    loadShipmentPlan();
  }, []);

  return (
    <>
      {state.tabExpeditionPlan && (
        <>
          <NavTitle>
            <Label title={t('loadDetailsPage.titlePage')} />
          </NavTitle>
          <div className="p-d-flex p-grid">
            {state.header.transportationType && (
              <CardView
                title={t(`global.transportationTypeShort${state.header.transportationType}`)}
                option={t(`global.transportationType${state.header.transportationType}`)}
              />
            )}
            <CardView
              title={state.header.expeditionType ? t(`global.expeditionTypeShort${state.header.expeditionType}`) : tr('global.expeditionTypeNotSpecified', 'Neuvedeno')}
              option={state.header.expeditionType ? t(`global.expeditionType${state.header.expeditionType}`) : ''}
            />
            {state.tabExpeditionPlan.truckType && <SelectedTruckType selectedTruck={state.tabExpeditionPlan.truckType} />}
            <CardView
              title={
                state.tabExpeditionPlan.loadingTypes &&
                state.tabExpeditionPlan.loadingTypes
                  .map(loadingType => {
                    return t(`global.manipulation.${loadingType}`);
                  })
                  .join(',')
              }
              option={t('expeditionOverviewPage.manipulationTitle')}
            />

            <CardView title={tr('TabExpeditionRoute.-Km', '- km')} option={t('expeditionOverviewPage.routeLength')} />
          </div>
          <ButtonLayout wrapper="WITH_PADDING_TOP_2">
            {state.tabExpeditionPlan.requirements.map((requirement, requirementIndex) => (
              <CheckboxItem className="p-md-3" key={requirementIndex}>
                <Checkbox checked={true} className="p-mr-2" disabled />
                <label>{requirement.name}</label>
              </CheckboxItem>
            ))}
          </ButtonLayout>
          <InfoProvided className="p-mt-3 flex flex-col gap-3">
            <InfoProvidedItem title={t(`TabExpeditionRoute.cargoType`, `Typ nákladu`)}>
              <p className="p-mt-1">
                {state.tabExpeditionPlan.cargoType?.name}
                {state.tabExpeditionPlan.expeditionHeaderPreview.adrWeight && (
                  <span> - {formatWeight(state.tabExpeditionPlan.expeditionHeaderPreview.adrWeight)}</span>
                )}
              </p>
            </InfoProvidedItem>

            {state.header.huEkaerNumber && (
              <InfoProvidedItem title={tr(`InputTextTag.ekaerNumber`, `Ekaer number`)} dataTestId="shipmentRoute-ekaerNumber">
                <p className="p-mt-1">{state.header.huEkaerNumber}</p>
              </InfoProvidedItem>
            )}
            {state.header.roUitNumber && (
              <InfoProvidedItem title={tr(`InputTextTag.uitNumber`, `Uit number`)} dataTestId="shipmentRoute-uitNumber">
                <p className="p-mt-1">{state.header.roUitNumber}</p>
              </InfoProvidedItem>
            )}
            {state.header.utnCode && (
              <InfoProvidedItem title={tr(`InputTextTag.utnCode`, `Utn code`)} dataTestId="shipmentRoute-utnCode">
                <p className="p-mt-1">{state.header.utnCode}</p>
              </InfoProvidedItem>
            )}
            {state.header.trackingNumber && (
              <InfoProvidedItem title={tr(`InputTextTag.trackingNumber`, `Tracking number`)} dataTestId="shipmentRoute-trackingNumber">
                <p className="p-mt-1">{state.header.trackingNumber}</p>
              </InfoProvidedItem>
            )}
            {state.header.polandSent && (
              <InfoProvidedItem title={tr(`PolandSentTag.title`, `Sent carrier`)} dataTestId="shipmentRoute-polandSent">
                <ul className="p-mt-1 list-disc pl-5">
                  {state.header.polandSent.sentNumber && (
                    <li data-testid="shipmentRoute-polandSent-sentNumber">
                      {tr(`PolandSentTag.sentNumber`, `Number`)}: {state.header.polandSent.sentNumber}
                    </li>
                  )}
                  {state.header.polandSent.sentCarrierUpdateKey && (
                    <li data-testid="shipmentRoute-polandSent-sentCarrierUpdateKey">
                      {tr(`IncotermsTag.sentCarrierUpdateKey`, `Update key`)}: {state.header.polandSent.sentCarrierUpdateKey}
                    </li>
                  )}
                </ul>
              </InfoProvidedItem>
            )}

            {state.tabExpeditionPlan.carrierNote && (
              <InfoProvidedItem title={t(`TabExpeditionRoute.carrierNote`, `Poznámka pro dopravce`)}>
                <CollapsibleNote html={state.tabExpeditionPlan.carrierNote} className="p-mt-1" />
              </InfoProvidedItem>
            )}
            {state.header.incoterms && (
              <InfoProvidedItem title={t(`DemandDetailComponent.incoterms`, `Incoterms`)}>
                <p>
                  <small>
                    <MultipleValues
                      values={[state.header.incoterms.incotermsValue, state.header.incoterms.incotermsDescription]}></MultipleValues>
                  </small>
                </p>
              </InfoProvidedItem>
            )}
          </InfoProvided>

          <div className="p-mt-3">
            <div className="p-mt-6">
              <Label title={t('expeditionOverviewPage.labelRoute')} />
              {/*// @ts-ignore*/}
              <Accordion multiple={true} activeIndex={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9]}>
                {state.tabExpeditionPlan.locations.map((location, index, locations) => {
                  return (
                    // @ts-ignore
                    <AccordionTab
                      key={index}
                      header={
                        <>
                          <AccordionTitle
                            number={index + 1}
                            title={location.name}
                            pretitle={location.address.city}
                            btnTitle={t('expeditionOverviewPage.accordionDetail')}
                          />
                        </>
                      }
                    >
                      {location.requirementCodes && location.requirementCodes.length > 0 && (
                        <div className="pb-2 gap-1">
                          <strong>{t('RouteDescriptionComponent.requirements', 'Požadavky')}:</strong>
                          {location.requirementCodes.map(req => tr('specialRequirement.' + req, req)).join(', ')}
                        </div>
                      )}
                      {location.loadedOrders?.length > 0 &&
                        location.loadedOrders.map((loadingOrders, orderIndex) => {
                          return (
                            <LoadingUloadingDetail
                              key={orderIndex}
                              loading={true}
                              order={loadingOrders}
                              carrierCode={location.pickupCode}
                              isFirstElement={orderIndex === 0}
                              showVolume={state.header?.volumeOfOrderEnabled}
                            />
                          );
                        })}
                      {location.unloadedOrders?.length > 0 &&
                        location.unloadedOrders.map((unloadedOrder, orderIndex) => {
                          return (
                            <LoadingUloadingDetail
                              key={orderIndex}
                              loading={false}
                              order={unloadedOrder}
                              carrierCode={location.pickupCode}
                              isFirstElement={orderIndex === 0}
                              showVolume={state.header?.volumeOfOrderEnabled}
                            />
                          );
                        })}

                      <ExpeditionContactInfo location={location} />
                    </AccordionTab>
                  );
                })}
              </Accordion>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TabExpeditionRoute;
